import {
	Badge,
	Button,
	Cell,
	Column,
	Row,
	TableBody,
	TableHeader,
	TableView,
	Text,
} from '@react-spectrum/s2';
import Add from '@react-spectrum/s2/icons/Add';
import { style } from '@react-spectrum/s2/style' with { type: 'macro' };
import type { Key } from 'react';
import { memo, useCallback, useMemo, useState } from 'react';

import { AnimateIn } from '@/components/AnimateIn';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import type { Campaign } from '../../../../bridge/types/campaign.ts';
import type { ProcessStatus } from '../../../../bridge/types/process.ts';
import { EmptyMessage } from '../content/EmptyMessage';

import { FilterToolbar } from './CampaignLibraryFilters';
import type { DimensionConfig } from './CampaignLibraryFilters';
import type { CampaignStatusFilter } from './campaignLibraryFilterUtils';
import { MetricPills } from './MetricPills';
import { PROCESS_STATUS_CONFIG } from './processConstants';
import { ProcessStatusLight } from './ProcessStatusLight';
import type { CampaignMetricsMap } from './useCampaigns';

const STATUS_DIMENSION: DimensionConfig = {
	key: 'status',
	label: 'Status',
	options: (
		Object.entries(PROCESS_STATUS_CONFIG) as [
			ProcessStatus,
			{ label: string },
		][]
	).map(([value, { label }]) => ({ label, value })),
};

const CAMPAIGN_DIMENSIONS: DimensionConfig[] = [STATUS_DIMENSION];

// True when the backend stamped strategy_id (the strategy it was activated from);
// absent on non-strategy campaigns, so the origin badge shows only on these rows.
const isStrategyBorn = (c: Campaign): boolean => !!c.strategy_id;

interface CampaignLibraryProps {
	campaigns: Campaign[];
	metrics: CampaignMetricsMap;
	metricsError?: string | null;
	onCampaignClick: (campaign: Campaign) => void;
	onCreateCampaign: () => void;
	animateIn?: boolean;
}

export const CampaignLibrary = memo(function CampaignLibrary({
	campaigns,
	metrics,
	metricsError,
	onCampaignClick,
	onCreateCampaign,
	animateIn = true,
}: CampaignLibraryProps) {
	const strategiesEnabled = useFeatureFlag('strategies');
	// Memoized because `campaigns ?? []` is a fresh array on every render, which
	// would defeat the memo below that depends on it.
	const safeCampaigns = useMemo(() => campaigns ?? [], [campaigns]);
	const normalizedCampaigns = useMemo(
		() => safeCampaigns.map((c) => ({ ...c, status: c.status ?? 'proposed' })),
		[safeCampaigns],
	);
	const [filters, setFilters] = useState<CampaignStatusFilter[]>([]);

	const addFilter = useCallback((filter: CampaignStatusFilter) => {
		setFilters((prev) => {
			if (prev.some((f) => f.value === filter.value)) return prev;
			return [...prev, filter];
		});
	}, []);

	const removeFilter = useCallback((filter: CampaignStatusFilter) => {
		setFilters((prev) => prev.filter((f) => f.value !== filter.value));
	}, []);

	const filteredCampaigns = useMemo(() => {
		if (filters.length === 0) return normalizedCampaigns;
		return normalizedCampaigns.filter((c) =>
			filters.some((f) => c.status === f.value),
		);
	}, [normalizedCampaigns, filters]);

	const activeCount = useMemo(
		() => normalizedCampaigns.filter((c) => c.status === 'active').length,
		[normalizedCampaigns],
	);

	if (safeCampaigns.length === 0) {
		return <EmptyMessage content="Your campaigns will be displayed here" />;
	}

	const Wrapper = animateIn ? AnimateIn : PassThrough;

	const handleAction = (key: Key) => {
		const campaign = safeCampaigns.find((c) => c.id === key);
		if (campaign) onCampaignClick(campaign);
	};

	return (
		<div className="flex h-full min-h-0 flex-col gap-3">
			<Wrapper delay={0}>
				<div className="flex shrink-0 flex-col gap-10">
					<div className="flex items-center justify-between">
						<Text
							styles={style({
								font: 'body',
								fontSize: 'body-sm',
								color: 'body',
							})}
						>
							{activeCount} Active
						</Text>
						<Button variant="primary" onPress={onCreateCampaign}>
							<Add />
							<Text>Create new</Text>
						</Button>
					</div>
					<FilterToolbar
						filters={filters}
						dimensions={CAMPAIGN_DIMENSIONS}
						onAdd={addFilter}
						onRemove={removeFilter}
					/>
				</div>
			</Wrapper>

			<div className="flex min-h-0 flex-1 flex-col">
				<Wrapper delay={1} className="flex min-h-0 flex-1 flex-col">
					{filteredCampaigns.length === 0 ? (
						<div className="flex flex-col items-center gap-3 py-12">
							<span className="text-sm text-(--text-muted)">
								No campaigns match your filters
							</span>
							<button
								type="button"
								onClick={() => setFilters([])}
								className="cursor-pointer text-sm text-(--text-secondary)
									underline hover:text-(--text-primary)"
							>
								Clear filters
							</button>
						</div>
					) : (
						<div
							className="flex min-h-0 flex-1 flex-col
								[&_[role=columnheader]_button]:pointer-events-none
								[&_[role=columnheader]_svg]:hidden **:[[role=gridcell]]:py-3
								**:[[role=row]]:cursor-pointer"
						>
							<TableView
								aria-label="Campaigns"
								styles={style({ width: 'full', height: 'full' })}
								onAction={handleAction}
								overflowMode="wrap"
								isQuiet
								density="spacious"
							>
								<TableHeader>
									<Column
										id="name"
										isRowHeader
										defaultWidth="2fr"
										allowsResizing
									>
										Campaign
									</Column>
									<Column id="status" allowsResizing>
										Status
									</Column>
									<Column id="metrics" allowsResizing>
										Key Results
									</Column>
								</TableHeader>
								<TableBody items={filteredCampaigns}>
									{(campaign) => (
										<Row id={campaign.id} data-campaign-id={campaign.id}>
											<Cell>
												<span className="inline-flex items-center gap-2">
													{campaign.title || campaign.goal}
													{strategiesEnabled && isStrategyBorn(campaign) && (
														<Badge
															variant="neutral"
															size="S"
															fillStyle="subtle"
															data-testid="strategy-origin-badge"
														>
															From strategy
														</Badge>
													)}
												</span>
											</Cell>
											<Cell>
												<ProcessStatusLight status={campaign.status} />
											</Cell>
											<Cell>
												<MetricPills
													metrics={metrics[campaign.id]}
													error={metricsError}
												/>
											</Cell>
										</Row>
									)}
								</TableBody>
							</TableView>
						</div>
					)}
				</Wrapper>
			</div>
		</div>
	);
});

const PassThrough = ({ children }: { children: React.ReactNode }) => (
	<>{children}</>
);
